import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { HelpCircle, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { useToast } from '@/hooks/use-toast'

export function SetupGuideDialog() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [copiedScript, setCopiedScript] = useState(false)

    const handleCopyScript = async () => {
        const script = t('servicenowCmdb.setupBusinessRuleScript')
        await navigator.clipboard.writeText(script)
        setCopiedScript(true)
        toast({ title: 'Copied', description: 'Script copied to clipboard' })
        setTimeout(() => setCopiedScript(false), 2000)
    }

    const fields = [
        {
            name: t('servicenowCmdb.setupMvcField'),
            defaultFieldName: 'u_mvc',
            type: t('servicenowCmdb.setupMvcType'),
            purpose: t('servicenowCmdb.setupMvcPurpose'),
            setBy: t('servicenowCmdb.setupMvcSetBy'),
        },
        {
            name: t('servicenowCmdb.setupMvcDateField'),
            defaultFieldName: 'u_mvc_tagged_date',
            type: t('servicenowCmdb.setupMvcDateType'),
            purpose: t('servicenowCmdb.setupMvcDatePurpose'),
            setBy: t('servicenowCmdb.setupMvcDateSetBy'),
        },
        {
            name: t('servicenowCmdb.setupVsIdField'),
            defaultFieldName: 'u_vault_sentinel_id',
            type: t('servicenowCmdb.setupVsIdType'),
            purpose: t('servicenowCmdb.setupVsIdPurpose'),
            setBy: t('servicenowCmdb.setupVsIdSetBy'),
        },
    ]

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                    <HelpCircle className="h-4 w-4 mr-1.5" />
                    {t('servicenowCmdb.setupGuideTitle')}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t('servicenowCmdb.setupGuideTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('servicenowCmdb.setupGuideDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    {/* Custom fields table */}
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('servicenowCmdb.setupFieldName')}</TableHead>
                                    <TableHead>{t('servicenowCmdb.setupFieldType')}</TableHead>
                                    <TableHead>{t('servicenowCmdb.setupFieldPurpose')}</TableHead>
                                    <TableHead>{t('servicenowCmdb.setupFieldSetBy')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {fields.map((field) => (
                                    <TableRow key={field.defaultFieldName}>
                                        <TableCell>
                                            <div>
                                                <span className="font-medium">{field.name}</span>
                                                <div className="text-xs text-muted-foreground font-mono mt-0.5">{field.defaultFieldName}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-xs">{field.type}</Badge>
                                        </TableCell>
                                        <TableCell className="text-sm">{field.purpose}</TableCell>
                                        <TableCell className="text-sm">{field.setBy}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Business Rule section */}
                    <div className="rounded-md border p-4 bg-muted/30">
                        <h4 className="text-sm font-semibold mb-1">{t('servicenowCmdb.setupBusinessRule')}</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                            {t('servicenowCmdb.setupBusinessRuleDescription')}
                        </p>
                        <div className="space-y-2 text-sm">
                            <div className="flex gap-2">
                                <span className="text-muted-foreground min-w-[60px]">Name:</span>
                                <span className="font-medium">{t('servicenowCmdb.setupBusinessRuleName')}</span>
                            </div>
                            <div className="flex gap-2">
                                <span className="text-muted-foreground min-w-[60px]">Trigger:</span>
                                <span>{t('servicenowCmdb.setupBusinessRuleTrigger')}</span>
                            </div>
                            <div className="flex gap-2 items-start">
                                <span className="text-muted-foreground min-w-[60px]">Script:</span>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <code className="bg-background border rounded px-2 py-1 text-xs font-mono flex-1">
                                            {t('servicenowCmdb.setupBusinessRuleScript')}
                                        </code>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleCopyScript}>
                                            {copiedScript ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Note */}
                    <p className="text-xs text-muted-foreground">
                        {t('servicenowCmdb.setupNote')}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    )
}
